import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Send, Check, Mail, Phone, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

const SUBJECTS = [
  { value: "info", label: "Demande d'information" },
  { value: "self-oneshot", label: "Commande Self-hosted (one-shot)" },
  { value: "self-subscription", label: "Commande Self-hosted + Updates" },
  { value: "saas", label: "Commande SaaS" },
  { value: "support", label: "Support technique" },
  { value: "other", label: "Autre" },
];

export default function ContactPage() {
  const [params] = useSearchParams();
  const initialSubject = (() => {
    const p = params.get("plan");
    if (p === "self-oneshot" || p === "self-subscription" || p === "saas") return p;
    return "info";
  })();

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: initialSubject,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Nom, email et message sont obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
      toast.success("Message envoyé. Nous vous répondons sous 24h.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="contact-page">
      <PublicHeader />

      <section className="max-w-3xl mx-auto px-6 pt-16 pb-8 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">// Contactez-nous</p>
        <h1 className="text-5xl font-black tracking-tight mb-6">Une question ? Un projet ?</h1>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto">
          Notre équipe vous répond sous 24h ouvrées. Pour commander, sélectionnez le plan correspondant.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-16">
        {submitted ? (
          <div className="text-center bg-card border-2 border-primary rounded-xl p-12" data-testid="contact-success">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Message envoyé !</h2>
            <p className="text-muted-foreground mb-6">Merci pour votre message. Nous vous répondons sous 24h ouvrées.</p>
            <Link to="/" className="text-primary hover:underline">Retour à l&apos;accueil →</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-card border border-border rounded-xl p-8 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium block mb-2">Nom complet *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full h-11 px-4 rounded-md border border-border bg-background"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full h-11 px-4 rounded-md border border-border bg-background"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Société (optionnel)</label>
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full h-11 px-4 rounded-md border border-border bg-background"
                  data-testid="contact-company"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Téléphone (optionnel)</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full h-11 px-4 rounded-md border border-border bg-background"
                  data-testid="contact-phone"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Type de demande</label>
              <select
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full h-11 px-4 rounded-md border border-border bg-background"
                data-testid="contact-subject"
              >
                {SUBJECTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Message *</label>
              <textarea
                required
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 rounded-md border border-border bg-background resize-y"
                placeholder="Décrivez votre besoin, votre projet, ou votre question..."
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="h-12 px-6 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="contact-submit"
            >
              <Send className="h-4 w-4" /> {submitting ? "Envoi..." : "Envoyer le message"}
            </button>
          </form>
        )}

        <div className="grid sm:grid-cols-3 gap-4 mt-8 text-sm">
          <div className="flex items-center gap-3 p-4 border border-border rounded-lg bg-card">
            <Mail className="h-5 w-5 text-primary shrink-0" />
            <a href="mailto:info@netkom.fr" className="hover:text-primary">info@netkom.fr</a>
          </div>
          <div className="flex items-center gap-3 p-4 border border-border rounded-lg bg-card">
            <Phone className="h-5 w-5 text-primary shrink-0" />
            <span className="text-muted-foreground">Réponse en 24h ouvrées</span>
          </div>
          <div className="flex items-center gap-3 p-4 border border-border rounded-lg bg-card">
            <MapPin className="h-5 w-5 text-primary shrink-0" />
            <span className="text-muted-foreground">France</span>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
